import { Rect } from '../lib/region';
import { sortByIndex } from '../render/utils/node';
import { TYPES } from '../render/constants';
import { Action } from './action';

function condition() {
  return true;
}

export const createGroupAction = new Action({
  name: 'Group',
  handler: _createGroupAction,
  shortcuts: 'Ctrl+G',
  condition: condition,
});

export const unGroupAction = new Action({
  name: 'UnGroup',
  handler: _unGroupAction,
  shortcuts: 'Ctrl+Shift+G',
  condition: condition,
  icon: 'group',
});

function _createGroupAction(stageContext) {
  const selectedNodes = stageContext.api.getSelectedNodes();
  const items = [...selectedNodes];
  items.sort(sortByIndex);

  const itemBoxes = items.map((item) => stageContext.api.getNodeBox(item));
  const rect = Rect.from(itemBoxes[0]);

  itemBoxes.forEach((box) => rect.expandRect(box));
  rect.inflate(0);

  const parentIds = items.map((item) => item.parentId).filter((id) => id);
  const parentId = parentIds[0] || null;
  const nextParent = stageContext.api.getNode(parentId);
  const nextParentBox = stageContext.api.getNodeBox(nextParent);

  const beforeX = nextParentBox?.x || 0;
  const beforeY = nextParentBox?.y || 0;

  const itemIndex = items[0].index;
  const groupIndex = stageContext.api.generateKeyBetween(itemIndex, null);
  const groupData = createGroup({
    x: rect.x - beforeX,
    y: rect.y - beforeY,
    w: rect.w,
    h: rect.h,
    parentId,
    index: groupIndex,
  });

  const group = stageContext.api.addNode(groupData);

  (() => {
    let lastIndex = groupIndex;
    items.forEach((item, i) => {
      const box = itemBoxes[i];
      const deltaX = box.x - rect.x;
      const deltaY = box.y - rect.y;
      const index = stageContext.api.generateKeyBetween(lastIndex, null);
      lastIndex = index;
      stageContext.api.updateNode(item.id, {
        parentId: group.id,
        index: index,
        x: deltaX,
        y: deltaY,
      });
    });
  })();

  updateGroupSize(stageContext, [
    {
      node: items[0],
    },
  ]);
  stageContext.api.selectNode(group.id);
}

function createGroup({ w = 1, h = 1, x = 0, y = 0, parentId } = {}) {
  return {
    x: x,
    y: y,
    w: w,
    h: h,
    parentId: parentId,
    type: TYPES.group,
  };
}

function _unGroupAction(store) {
  const selectedNodes = store.api.getSelectedNodes();
  const groups = selectedNodes.filter((node) => node.type === TYPES.group);
  if (groups.length === 0) return;

  const childrenNodes = [];
  store.api.transact(() => {
    groups.forEach((group) => {
      const children = store.api.getChildren(group.id);
      children.forEach((child) => {
        childrenNodes.push(child);
        store.api.updateNode(child.id, {
          parentId: group.parentId,
          x: group.x + child.x,
          y: group.y + child.y,
        });
      });
    });
  });
  store.api.transact(() => {
    groups.forEach((group) => {
      store.api.removeNode(group.id);
    });
  });
  store.api.clearSelect();
  childrenNodes.forEach((node) => {
    store.api.selectNode(node, true);
  });
}

export function removeOnlyOneChildGroup(stageContext, groups) {
  if (!groups || !groups.length) return [];

  const result = [];

  groups.forEach((group) => {
    if (!group || group.type !== TYPES.group) return;

    const children = stageContext.api.getChildren(group.id);

    if (!children.length) {
      stageContext.api.removeNode(group.id);
    }

    // If the group has exactly one child, remove the group and adjust the child
    else if (children.length === 1) {
      const child = children[0];

      // Update the child's position to be relative to the group's parent
      stageContext.api.updateNode(child.id, {
        parentId: group.parentId,
        x: group.x + child.x,
        y: group.y + child.y,
      });

      // Remove the group
      stageContext.api.removeNode(group.id);

      // Add the child to the result for further processing if needed
      result.push({
        node: child,
        id: child.id,
        x: child.x,
        y: child.y,
        w: child.w,
        h: child.h,
      });
    }
  });

  return result;
}

export function updateGroupSize(stageContext, nodes) {
  const groupNodeIds = Array.from(
    new Set(nodes.map((item) => item.node.parentId).filter((id) => id))
  );
  const groupNodes = groupNodeIds
    .map((id) => stageContext.api.getNode(id))
    .filter((n) => !!n)
    .filter((n) => n.type === TYPES.group);

  const { interactive } = stageContext;
  groupNodes.forEach((groupNode) => {
    const children = stageContext.api.getChildren(groupNode.id);
    if (!children.length) return;

    // Use transform-aware world-space regions for children
    let unionWorld = null;
    for (const child of children) {
      const region = stageContext.api.getNodeRegion(child);
      if (!region) continue;
      const rect = region.rect;
      if (!unionWorld) {
        unionWorld = Rect.from(rect);
      } else {
        unionWorld.expandRect(rect);
      }
    }
    if (!unionWorld) return;

    // Convert world-space union rect to group's parent local space (assuming parent has no transform)
    // If parent exists, subtract its world-space box origin; otherwise keep as is
    const parentNode = groupNode.parentId ? stageContext.api.getNode(groupNode.parentId) : null;
    const parentBox = parentNode ? stageContext.api.getNodeBox(parentNode) : { x: 0, y: 0 };
    const newGroupX = Math.round(unionWorld.x - (parentBox?.x || 0));
    const newGroupY = Math.round(unionWorld.y - (parentBox?.y || 0));
    const newGroupW = Math.round(unionWorld.w);
    const newGroupH = Math.round(unionWorld.h);

    const groupDeltaX = newGroupX - groupNode.x;
    const groupDeltaY = newGroupY - groupNode.y;

    const positionChanged = groupDeltaX !== 0 || groupDeltaY !== 0;
    const sizeChanged = newGroupW !== groupNode.w || newGroupH !== groupNode.h;

    if (positionChanged) {
      // Shift children inversely to preserve their world positions
      const shiftX = -groupDeltaX;
      const shiftY = -groupDeltaY;
      children.forEach((node) => {
        stageContext.api.updateNode(node.id, {
          x: node.x + shiftX,
          y: node.y + shiftY,
        });
        const cache = interactive.getMovingNodeInitialBox(node.id);
        if (cache) {
          cache.x += shiftX;
          cache.y += shiftY;
        }
      });
    }

    if (positionChanged || sizeChanged) {
      stageContext.api.updateNode(groupNode.id, {
        x: newGroupX,
        y: newGroupY,
        w: newGroupW,
        h: newGroupH,
      });
    }
  });

  return groupNodes;
}
